import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useCustomerAuth } from '@/hooks/useCustomerAuth';
import { useTheme } from '@/hooks/useTheme';
import { getFavouriteCount } from '@/hooks/useFavourites';
import CustomerLoginModal from '@/components/feature/CustomerLoginModal';

interface BottomNavProps {
  cartCount?: number;
  onOpenCart?: () => void;
}

export default function BottomNav({ cartCount = 0, onOpenCart }: BottomNavProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const { isLoggedIn, user } = useCustomerAuth();
  const { colors } = useTheme();
  const [showLoginModal, setShowLoginModal] = useState(false);
  const favCount = getFavouriteCount();

  const isActive = (path: string) => location.pathname === path;

  const handleProfileClick = () => {
    if (isLoggedIn) {
      navigate('/profile');
    } else {
      setShowLoginModal(true);
    }
  };

  const navItems = [
    {
      id: 'home',
      label: 'Home',
      icon: 'ri-home-5-line',
      activeIcon: 'ri-home-5-fill',
      action: () => navigate('/'),
      active: isActive('/'),
    },
    {
      id: 'shop',
      label: 'Shop',
      icon: 'ri-store-2-line',
      activeIcon: 'ri-store-2-fill',
      action: () => navigate('/category'),
      active: isActive('/category'),
    },
    {
      id: 'cart',
      label: 'Cart',
      icon: 'ri-shopping-bag-line',
      activeIcon: 'ri-shopping-bag-fill',
      action: onOpenCart,
      active: false,
      badge: cartCount > 0 ? cartCount : undefined,
    },
    {
      id: 'favourites',
      label: 'Saved',
      icon: 'ri-heart-line',
      activeIcon: 'ri-heart-fill',
      action: () => navigate('/'),
      active: false,
      badge: favCount > 0 ? favCount : undefined,
      heartRed: favCount > 0,
    },
    {
      id: 'profile',
      label: isLoggedIn ? (user?.name?.split(' ')[0] || 'Profile') : 'Login',
      icon: 'ri-user-line',
      activeIcon: 'ri-user-fill',
      action: handleProfileClick,
      active: isActive('/profile'),
      isLogin: !isLoggedIn,
    },
  ];

  return (
    <>
      {/* Bottom Nav — mobile only */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 z-50 bg-white dark:bg-slate-900 border-t border-gray-100 dark:border-slate-800 safe-area-pb">
        <div className="flex items-stretch h-16">
          {navItems.map((item) => {
            const isCartItem = item.id === 'cart';
            const isLoginItem = item.isLogin;

            if (isCartItem) {
              return (
                <button
                  key={item.id}
                  onClick={item.action}
                  className="flex-1 flex flex-col items-center justify-center gap-0.5 relative cursor-pointer"
                >
                  {/* Cart pill — special center button */}
                  <div
                    className="relative w-12 h-12 rounded-full flex items-center justify-center -mt-5 shadow-lg"
                    style={{ background: `linear-gradient(135deg, ${colors.primary}, ${colors.secondary})` }}
                  >
                    <i className="ri-shopping-bag-fill text-white text-xl"></i>
                    {item.badge !== undefined && (
                      <span className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-red-500 text-white text-[10px] flex items-center justify-center font-bold border-2 border-white">
                        {item.badge > 9 ? '9+' : item.badge}
                      </span>
                    )}
                  </div>
                  <span className="text-[10px] font-semibold text-gray-500 dark:text-gray-400 mt-0.5">
                    {item.label}
                  </span>
                </button>
              );
            }

            if (isLoginItem) {
              return (
                <button
                  key={item.id}
                  onClick={item.action}
                  className="flex-1 flex flex-col items-center justify-center gap-0.5 relative cursor-pointer"
                >
                  <div className="relative w-8 h-8 flex items-center justify-center rounded-full"
                    style={{ background: `${colors.primary}20` }}>
                    <i className="ri-user-line text-lg" style={{ color: colors.primary }}></i>
                    {/* Ping dot for login */}
                    <span className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-yellow-400 opacity-75"></span>
                      <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-yellow-400 border border-white"></span>
                    </span>
                  </div>
                  <span className="text-[10px] font-bold" style={{ color: colors.primary }}>
                    Login
                  </span>
                </button>
              );
            }

            return (
              <button
                key={item.id}
                onClick={item.action}
                className="flex-1 flex flex-col items-center justify-center gap-0.5 relative cursor-pointer group"
              >
                {/* Active indicator */}
                {item.active && (
                  <span
                    className="absolute top-0 left-1/2 -translate-x-1/2 w-8 h-0.5 rounded-full"
                    style={{ backgroundColor: colors.primary }}
                  ></span>
                )}
                <div className="relative w-8 h-8 flex items-center justify-center">
                  <i
                    className={`text-xl transition-all ${
                      item.active
                        ? item.activeIcon
                        : item.icon
                    } ${item.heartRed ? 'text-red-500' : ''}`}
                    style={item.active && !item.heartRed ? { color: colors.primary } : {}}
                  ></i>
                  {item.badge !== undefined && (
                    <span className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-red-500 text-white text-[9px] flex items-center justify-center font-bold">
                      {item.badge > 9 ? '9+' : item.badge}
                    </span>
                  )}
                </div>
                <span
                  className={`text-[10px] font-semibold transition-all max-w-[56px] truncate ${
                    item.active
                      ? ''
                      : 'text-gray-400 dark:text-gray-500'
                  }`}
                  style={item.active ? { color: colors.primary } : {}}
                >
                  {item.label}
                </span>
              </button>
            );
          })}
        </div>
      </nav>

      {/* Spacer so content doesn't hide behind bottom nav on mobile */}
      <div className="md:hidden h-16"></div>

      <CustomerLoginModal
        isOpen={showLoginModal}
        onClose={() => setShowLoginModal(false)}
        onSuccess={() => setShowLoginModal(false)}
        title="Welcome to ClothyBazar"
        subtitle="Login or register with phone or email"
      />
    </>
  );
}
